import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Box, Typography } from '@material-ui/core';
import useStyles from './DateRangePicker.styled';
import { DateRangePicker } from 'react-dates';
import 'react-dates/lib/css/_datepicker.css';
import { useSelector } from 'react-redux';
import 'react-dates/initialize';
import moment from 'moment';

// Slightly longer than the MUI enter transition (225ms), so an ancestor that is animating when
// the calendar opens has settled before we ask react-dates to measure the input again.
const REANCHOR_DELAY_MS = 300;

// Rendered height of the two-month calendar: the 280px transition container plus the gutter
// react-dates leaves between the input and the popup.
const CALENDAR_HEIGHT = 332;

const RangePicker = ({
  label,
  endDate,
  startDate,
  onDatesChange,
  onClose,
  showClearDates,
  disabled,
  appendToBody,
  anchorDirection,
}) => {
  const classes = useStyles();
  const containerRef = useRef(null);
  const [focusedInput, setFocusedInput] = useState(null);
  const [openDirection, setOpenDirection] = useState('down');
  const reanchorTimer = useRef(null);
  const { localeFormat } = useSelector((state) => state.organizations);

  useEffect(() => () => clearTimeout(reanchorTimer.current), []);

  /**
   * While the detached calendar is open react-dates locks scrolling on every ancestor, so
   * anything below the fold is unreachable. Escape is then the only way out — MUI's own handler
   * never sees the key, because focus sits in a portal outside the modal.
   */
  useEffect(() => {
    if (!focusedInput) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setFocusedInput(null);
    };

    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [focusedInput]);

  /**
   * Handle date range picker focus
   */
  const onFocusChange = (nextFocusedInput) => {
    const isOpening = !focusedInput && nextFocusedInput;

    // Opened from a field low in the viewport the calendar would hang past the bottom edge,
    // and with scrolling locked those days cannot be reached — so flip it above the input.
    if (isOpening && containerRef.current) {
      const { top, bottom } = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - bottom;
      setOpenDirection(spaceBelow < CALENDAR_HEIGHT && top > spaceBelow ? 'up' : 'down');
    }

    setFocusedInput(nextFocusedInput);

    if (!appendToBody) return;

    // A pending re-measure is pointless once the calendar is closed, and moving between the
    // start and end inputs does not move the calendar — only opening it does.
    if (!nextFocusedInput) {
      clearTimeout(reanchorTimer.current);
      return;
    }

    // With `appendToBody` react-dates measures the input once, when the calendar opens. If an
    // ancestor is still mid-transition at that moment its transform skews the measurement and
    // the calendar stays mis-anchored. A resize is the library's own re-measure trigger.
    if (isOpening) {
      reanchorTimer.current = setTimeout(
        () => window.dispatchEvent(new Event('resize')),
        REANCHOR_DELAY_MS
      );
    }
  };

  /**
   * Render day content
   * @param {*} date
   */
  const renderDayContents = (date) => (
    <span
      className={date.format(localeFormat) === moment().format(localeFormat) ? classes.isToday : ''}
    >
      {date.format('D')}
    </span>
  );

  return (
    <Box
      ref={containerRef}
      display="flex"
      data-cy="date-range"
      flexDirection="column"
      className={classes.container}
    >
      {label && (
        <Typography variant="body2" component="span" color="textPrimary" className={classes.label}>
          {label}
        </Typography>
      )}
      <DateRangePicker
        small={true}
        daySize={30}
        endDate={endDate}
        onClose={onClose}
        startDate={startDate}
        enableOutsideDays={true}
        showDefaultInputIcon={true}
        focusedInput={focusedInput}
        isOutsideRange={() => false}
        onDatesChange={onDatesChange}
        onFocusChange={onFocusChange}
        endDateId="calendar_end_date"
        showClearDates={showClearDates}
        startDateId="calendar_start_date"
        hideKeyboardShortcutsPanel={true}
        renderDayContents={renderDayContents}
        disabled={disabled}
        appendToBody={appendToBody}
        anchorDirection={anchorDirection}
        openDirection={openDirection}
      />
    </Box>
  );
};

RangePicker.propTypes = {
  label: PropTypes.string,
  disabled: PropTypes.bool,
  endDate: PropTypes.any,
  startDate: PropTypes.any,
  showClearDates: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onDatesChange: PropTypes.func.isRequired,
  // Renders the calendar in a body-level portal, still anchored to the input, so that a
  // scrolling ancestor (a modal) cannot clip it. react-dates re-measures on every open.
  appendToBody: PropTypes.bool,
  // Side of the input the calendar is aligned to; use 'right' when it sits near a right edge
  anchorDirection: PropTypes.oneOf(['left', 'right']),
};

RangePicker.defaultProps = {
  onClose: () => {},
  showClearDates: false,
  onDatesChange: () => {},
  appendToBody: false,
  anchorDirection: 'left',
};

export default RangePicker;
